import React, { useState, useRef } from 'react';
import { Save, X, FileText, FolderOpen, Users, Flag, Clock, Calendar, RotateCcw, Hash, Paperclip, Upload } from 'lucide-react';

// Helper: format date to YYYY-MM-DD for input type="date"
const toDateValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper: konversi tanggal ke format YYYY-MM-DDTHH:MM untuk input type="datetime-local"
const toDatetimeLocalValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ActivityForm = ({ onSubmit, initialData = null, onCancel }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    ticketId: initialData?.ticketId || '',
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || 'Work',
    bagian: initialData?.bagian || 'Umum',
    priority: initialData?.priority || 'Medium',
    status: initialData?.status || 'Pending',
    date: toDatetimeLocalValue(initialData?.date) || toDatetimeLocalValue(new Date()),
    endDate: toDatetimeLocalValue(initialData?.endDate) || toDatetimeLocalValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
    tanggal: toDateValue(initialData?.tanggal) || toDateValue(new Date()),
  });
  const [lampiranFile, setLampiranFile] = useState(null);
  const [lampiranName, setLampiranName] = useState(initialData?.lampiran || '');

  const categories = ['Work', 'Development', 'Meeting', 'Documentation', 'Research', 'Personal'];
  const bagians = ['Keuangan', 'HRD', 'IT', 'Operasional', 'Marketing', 'Produksi', 'Umum'];
  const priorities = ['Low', 'Medium', 'High'];
  const statuses = ['Pending', 'In Progress', 'Completed'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLampiranFile(file);
      setLampiranName(file.name);
    }
  };

  const handleRemoveFile = () => {
    setLampiranFile(null);
    setLampiranName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (lampiranFile) {
      submitData.lampiran = lampiranFile;
    }
    onSubmit(submitData);
    if (!initialData) {
      setFormData({
        title: '',
        description: '',
        category: 'Work',
        bagian: 'Umum',
        priority: 'Medium',
        status: 'Pending',
        date: toDatetimeLocalValue(new Date()),
        endDate: toDatetimeLocalValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
        tanggal: toDateValue(new Date()),
      });
      setLampiranFile(null);
      setLampiranName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    setFormData({
      ticketId: '',
      title: '',
      description: '',
      category: 'Work',
      bagian: 'Umum',
      priority: 'Medium',
      status: 'Pending',        date: toDatetimeLocalValue(new Date()),
        endDate: toDatetimeLocalValue(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
        tanggal: toDateValue(new Date())
    });
  };

  const SelectField = ({ label, icon: Icon, name, value, options, onChange }) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white focus:bg-white appearance-none cursor-pointer text-sm"
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {initialData ? 'Edit Activity' : 'Add New Activity'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {initialData ? 'Update the details of your activity' : 'Create a new activity to track your work'}
          </p>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="ml-4 p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="section-title">Basic Information</h3>
            </div>
          </div>
          <div className="card-body space-y-5">
            {/* Ticket ID (auto-generated, read-only when editing) */}
            {formData.ticketId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Ticket ID <span className="text-xs text-gray-400 font-normal">(Auto-generated by system)</span>
                </label>
                <div className="flex items-center space-x-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <code className="text-sm font-mono font-semibold text-gray-700">{formData.ticketId}</code>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5">
                Activity Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="e.g. Q1 Financial Report Review"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                maxLength={1000}
                className="input-field resize-y"
                placeholder="Describe the activity details..."
              />
              <div className="mt-1 flex justify-end">
                <span className={`text-xs ${formData.description.length >= 1000 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {formData.description.length}/1000 karakter
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Classification */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <FolderOpen className="w-5 h-5 text-purple-600" />
              <h3 className="section-title">Classification</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SelectField
                label="Category"
                icon={FolderOpen}
                name="category"
                value={formData.category}
                options={categories}
                onChange={handleChange}
              />
              <SelectField
                label="Bagian"
                icon={Users}
                name="bagian"
                value={formData.bagian}
                options={bagians}
                onChange={handleChange}
              />
              <SelectField
                label="Priority"
                icon={Flag}
                name="priority"
                value={formData.priority}
                options={priorities}
                onChange={handleChange}
              />
              <SelectField
                label="Status"
                icon={Clock}
                name="status"
                value={formData.status}
                options={statuses}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Tanggal Input */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <h3 className="section-title">Tanggal Input</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="max-w-xs">
              <label htmlFor="tanggal" className="block text-sm font-medium text-gray-700 mb-1.5">
                Tanggal <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="tanggal"
                name="tanggal"
                value={formData.tanggal}
                onChange={handleChange}
                required
                className="input-field"
              />
              <p className="mt-1 text-xs text-gray-400">Default: hari ini. Bisa di-backdate untuk input tanggal sebelumnya.</p>
            </div>
          </div>
        </div>

        {/* Attachment */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Paperclip className="w-5 h-5 text-orange-600" />
              <h3 className="section-title">Attachment</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="flex items-center gap-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx"
                className="hidden"
                id="activity-lampiran-input"
              />
              <label
                htmlFor="activity-lampiran-input"
                className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all text-sm font-medium text-gray-600 hover:text-blue-600"
              >
                <Upload className="w-4 h-4" />
                <span>Choose File</span>
              </label>
              {lampiranName ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Paperclip className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-700 truncate max-w-[200px]">{lampiranName}</span>
                  <button type="button" onClick={handleRemoveFile} className="text-red-400 hover:text-red-600 ml-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : initialData?.lampiran ? (
                <a
                  href={`http://localhost:5000${initialData.lampiran}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all"
                >
                  <Paperclip className="w-4 h-4" />
                  <span className="truncate max-w-[200px]">View current file</span>
                </a>
              ) : null}
              <span className="text-xs text-gray-400">PDF, JPG, PNG, Excel, Word (max 10MB)</span>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <h3 className="section-title">Schedule</h3>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Start Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1.5">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center">
          <div>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary"
              >
                <X className="w-4 h-4" />
                <span>Close</span>
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClear}
              className="btn-secondary"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              <Save className="w-4 h-4" />
              <span>{initialData ? 'Update Activity' : 'Save Activity'}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ActivityForm;
